import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, rest } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryClient';
import type { Page } from '@/types/database';

export function usePages(options?: { includeUnpublished?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.pages.list(), options],
    queryFn: async () => {
      let query = rest.from<Page>('pages')
        .select('*')
        .order('navbar_order', { ascending: true });

      if (!options?.includeUnpublished) {
        query = query.eq('status', 'published');
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Page[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePage(slug: string) {
  return useQuery({
    queryKey: queryKeys.pages.detail(slug),
    queryFn: async () => {
      const { data, error } = await rest.from<Page>('pages')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as Page;
    },
    enabled: !!slug,
  });
}

export function useCreatePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (page: Omit<Page, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('pages')
        .insert(page as any)
        .select()
        .single();

      if (error) throw error;
      return data as Page;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pages.all });
    },
  });
}

export function useUpdatePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Page> & { id: string }) => {
      const { data, error } = await supabase
        .from('pages')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Page;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pages.all });
      if (data?.slug) {
        queryClient.invalidateQueries({ queryKey: queryKeys.pages.detail(data.slug) });
      }
    },
  });
}

export function useDeletePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('pages')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pages.all });
    },
  });
}
